// ATLAS Industrial · i18n strings
// Key rules:
//  - Spanish (es-AR): Rioplatense voseo (Sabés, Solicitá, Conocé).
//  - Portuguese (pt-BR): Brazilian industrial tone.
//  - English (en): Professional industrial terminology (Asset Management).
//  - French (fr): Professional industrial terminology (Gestion des Actifs).
//
// Provincia options remain Argentine names regardless of language
// (the business operates from Argentina).

export const LANGUAGES = [
  { code: "es", label: "Español", nativeLabel: "Español (AR)", flag: "🇦🇷" },
  { code: "pt", label: "Português", nativeLabel: "Português (BR)", flag: "🇧🇷" },
  { code: "en", label: "English", nativeLabel: "English", flag: "🇺🇸" },
  { code: "fr", label: "Français", nativeLabel: "Français", flag: "🇫🇷" },
];

export const DEFAULT_LANG = "es";

export const translations = {
  es: {
    lang: "es-AR",
    nav: {
      soluciones: "Soluciones",
      casos: "Casos de Éxito",
      sectores: "Sectores",
      recursos: "Recursos",
      openMenu: "Abrir menú",
    },
    cta: {
      requestDemo: "Solicitá tu Demo",
      learnMore: "Conocé más",
      requestDemoLong: "Solicitá una Demo",
      whatsapp: "Contactar por WhatsApp",
      languageSwitcher: "Cambiar idioma",
    },
    hero: {
      title1: "ATLAS",
      title2a: "Plataforma digital de",
      title2b: "trazabilidad operativa y control de gestión en campo",
      subtitle: "NFC en el activo, checklists offline-first y reportería auditable en tiempo real.",
    },
    features: {
      f1: { title: "Escaneá & Verificá", desc: "Identificá cada activo con NFC." },
      f2: { title: "Checklists Inteligentes", desc: "Obligá procesos claros y seguros." },
      f3: { title: "Analizá & Decidí", desc: "Detectá fallas y patrones críticos." },
    },
    whatIs: {
      badge: "¿Qué es ATLAS?",
      titleA: "Una plataforma centrada en el",
      titleB: "activo industrial",
      paragraphs: [
        "**ATLAS** es una plataforma de gestión de mantenimiento centrada en el activo. Combina identificación física con tags **NFC**, procedimientos estructurados (**checklists**) y ejecución móvil para crear un proceso auditable, repetible y medible.",
        "En entornos industriales, logísticos y de infraestructura, el mantenimiento suele operar con registros manuales y dispersos. ATLAS elimina la **identificación incierta**, los **procedimientos desestructurados**, la **trazabilidad pobre**, la **falta de evidencia** y los **patrones invisibles** de falla.",
        "El resultado: tu equipo de campo trabaja con la misma información que tu gerencia, en tiempo real, incluso en planta sin señal.",
      ],
    },
    faq: {
      badge: "Preguntas Clave",
      titleA: "¿Qué te permite",
      titleB: "saber ATLAS?",
      subtitle:
        "Las preguntas que toda gerencia industrial debería poder responder en menos de 30 segundos. Tocá cada una para ver cómo ATLAS las resuelve.",
      items: [
        {
          q: "¿Qué activo se intervino, quién lo hizo y cuándo?",
          a: "Cada intervención queda asociada al activo (vía NFC), al técnico (login + rol) y al timestamp del servidor. Trazabilidad completa sin planilla en papel.",
        },
        {
          q: "¿Se siguieron los pasos correctos del procedimiento?",
          a: "Los checklists son obligatorios paso a paso. Si falta una lectura, foto o firma, la app no permite cerrar la tarea. La evidencia queda guardada por intervención.",
        },
        {
          q: "¿Qué fallas se están repitiendo y dónde?",
          a: "El motor de análisis cruza hallazgos por activo, área y especialidad. Detectás patrones críticos antes de que se conviertan en parada no programada.",
        },
        {
          q: "¿Cuál es el costo real de mantener cada activo?",
          a: "ATLAS consolida horas-hombre, repuestos consumidos y frecuencia de intervención por activo. Tomás decisiones de reemplazo con datos, no con intuición.",
        },
        {
          q: "¿La planta está lista para una auditoría externa?",
          a: "Reportes exportables con firma digital, evidencia fotográfica, lecturas numéricas y trazabilidad temporal. 100% auditables ISO/IRAM.",
        },
        {
          q: "¿Mis técnicos trabajan igual en zonas sin señal?",
          a: "Sí. La app es offline-first: ejecuta checklists, captura fotos y graba lecturas sin red, y sincroniza apenas vuelve la conectividad.",
        },
      ],
    },
    howItWorks: {
      badge: "Cómo Funciona",
      titleA: "ATLAS en 10 pasos,",
      titleB: "del activo al reporte",
      subtitle:
        "Un flujo claro y predecible que tus técnicos adoptan en una sola jornada de campo.",
      colStep: "Paso",
      colAction: "Acción",
      colDetail: "Detalle",
      steps: [
        { action: "Identificación física del activo", detail: "Se adhiere un tag NFC único en una zona limpia y accesible del activo." },
        { action: "Registro en el portal", detail: "El supervisor asocia el tag al activo, su área, especialidad y plantillas de checklist." },
        { action: "Asignación de checklists", detail: "Plantillas parametrizables se vinculan a uno o varios activos según tipo y criticidad." },
        { action: "Escaneo NFC en campo", detail: "El técnico acerca el celular al tag y la app carga el activo, el historial y las tareas pendientes." },
        { action: "Validación de rol", detail: "ATLAS verifica especialidad y permisos antes de mostrar el checklist correspondiente." },
        { action: "Ejecución paso a paso", detail: "Lecturas, fotos, firmas y observaciones se capturan en orden, con campos obligatorios." },
        { action: "Modo offline", detail: "Si no hay señal, la app guarda todo localmente. Nada se pierde, nada se interrumpe." },
        { action: "Sincronización automática", detail: "Al recuperar conectividad, ATLAS envía la información al servidor con timestamp original." },
        { action: "Análisis & detección de patrones", detail: "El motor de análisis cruza datos por activo, área y especialidad para detectar fallas recurrentes." },
        { action: "Reporte & auditoría", detail: "Reportes exportables con evidencia, firma digital y trazabilidad total para auditorías internas o externas." },
      ],
    },
    areas: {
      badge: "¿Qué puede gestionar ATLAS?",
      titleA: "Cualquier activo crítico,",
      titleB: "en cualquier industria",
      subtitle:
        "Desde flota pesada hasta líneas de producción, ATLAS se adapta a los activos que sostienen tu operación.",
      items: [
        { title: "Equipos de producción", desc: "Bombas, motores, compresores y maquinaria de línea con mantenimiento preventivo y correctivo." },
        { title: "Flota pesada & vehicular", desc: "Camiones, perforadoras, grúas y maquinaria móvil con trazabilidad de horas y kilometraje." },
        { title: "Activos eléctricos", desc: "Transformadores, tableros, generadores y sistemas eléctricos con inspecciones programadas." },
        { title: "Instalaciones de planta", desc: "Cañerías, tanques, válvulas y estructuras civiles con inspección visual + lecturas." },
        { title: "Herramientas críticas", desc: "Torquímetros, manómetros e instrumental con calibración y vencimientos controlados." },
        { title: "Stock & repuestos", desc: "Control de inventario asociado a cada intervención, con consumo trazable por activo." },
        { title: "Seguridad e higiene", desc: "Checklists de EPP, permisos de trabajo, bloqueo & etiquetado (LOTO) auditables." },
        { title: "Calibraciones & ensayos", desc: "Lecturas numéricas con tolerancias parametrizadas y alertas automáticas fuera de rango." },
        { title: "Edificios & facilities", desc: "Mantenimiento de aires, iluminación, sanitarios y áreas comunes en complejos industriales." },
        { title: "Energías renovables", desc: "Aerogeneradores, paneles solares e instalaciones distribuidas en sitios remotos." },
        { title: "Servicios tercerizados", desc: "Trazabilidad de contratistas: quién entró, qué hizo y con qué evidencia respalda la tarea." },
      ],
    },
    checklistsTable: {
      badge: "Checklists Personalizados",
      titleA: "Plantillas adaptadas",
      titleB: "a tu operación",
      subtitle:
        "ATLAS soporta cualquier tipo de paso obligatorio. Diseñá la plantilla una vez y replicala en miles de intervenciones.",
      colType: "Tipo de Paso",
      colExample: "Ejemplo",
      colBenefit: "Beneficio",
      rows: [
        { type: "Lectura numérica", example: "Presión, temperatura, vibración, torque", benefit: "Tolerancias parametrizadas; alerta automática si está fuera de rango." },
        { type: "Captura fotográfica", example: "Estado de la bomba antes y después", benefit: "Evidencia visual con timestamp y geolocalización." },
        { type: "Checkbox de verificación", example: "Limpieza, ajuste, lubricación", benefit: "Tareas obligatorias; no se cierra el checklist sin completar." },
        { type: "Firma digital", example: "Conformidad del técnico y supervisor", benefit: "Validación legal con sello horario del servidor." },
        { type: "Texto libre & observaciones", example: "Notas sobre hallazgos imprevistos", benefit: "Contexto cualitativo enriquecido por el técnico." },
        { type: "Lectura de código QR/NFC", example: "Identificación de repuesto consumido", benefit: "Trazabilidad de stock atado al activo intervenido." },
      ],
    },
    offline: {
      badge: "Funcionamiento Sin Conexión",
      titleA: "Diseñado para",
      titleB: "entornos sin señal",
      subtitle:
        "Plantas remotas, túneles, depósitos, parques eólicos. Tu equipo trabaja igual, con o sin red.",
      items: [
        { title: "Captura 100% offline", desc: "Lecturas, fotos y firmas se guardan localmente en el dispositivo del técnico." },
        { title: "Sincronización inteligente", desc: "Apenas vuelve la conectividad, ATLAS sube todo automáticamente sin intervención del usuario." },
        { title: "Cache local cifrado", desc: "Los datos quedan protegidos en el dispositivo con cifrado AES estándar industria." },
        { title: "Integridad de timestamp", desc: "El sello horario de la captura se conserva, no se sobreescribe al sincronizar." },
      ],
    },
    metrics: {
      qPre: "¿",
      qStrong1: "Sabés realmente",
      qMid: "qué está pasando en",
      qStrong2: "tu planta?",
      m1: "Trazabilidad",
      m2: "Fallas Recurrentes",
      m3: "Evidencia de Mantenimiento",
      m4: "Costos Operativos",
    },
    showcase: {
      titleA: "Llevá tu Mantenimiento al",
      titleB: "Siguiente Nivel",
      subtitle:
        "Empoderá a tus equipos de campo con una herramienta robusta, offline-first y pensada para entornos industriales exigentes.",
      bullets: [
        "Escaneá NFC en el Activo",
        "Checklists Paso a Paso",
        "Reportes & Alertas en Tiempo Real",
        "Historial de Mantenimientos",
      ],
    },
    cases: {
      badge: "Casos de Éxito",
      titleA: "Resultados comprobados en la",
      titleB: "industria argentina",
      subtitle:
        "Empresas líderes de distintos sectores ya están transformando su operación con ATLAS. Estos son algunos resultados reales.",
      c1: {
        industry: "Minería",
        title: "Yacimientos de Altura",
        resultLabel: "disponibilidad de flota",
        desc:
          "Optimización de activos en yacimiento de altura. Logramos un +25% de disponibilidad de flota mediante trazabilidad NFC.",
      },
      c2: {
        industry: "Energía",
        title: "Parques Eólicos",
        resultLabel: "tiempos de respuesta",
        desc:
          "Control de mantenimiento preventivo en parques eólicos. Reducción del 40% en tiempos de respuesta ante fallas críticas.",
      },
      c3: {
        industry: "Manufactura",
        title: "Planta Automotriz",
        resultLabel: "cumplimiento auditoría",
        desc:
          "Digitalización total de planta automotriz. Eliminación de registros en papel y cumplimiento del 100% de normas de auditoría.",
      },
    },
    finalCta: {
      titleA: "Llevá tu Mantenimiento al",
      titleB: "Siguiente Nivel",
      subtitle:
        "Solicitá una demo personalizada y descubrí cómo digitalizar el mantenimiento de tu planta en minutos.",
    },
    footer: {
      tagline:
        "Plataforma industrial para la gestión de activos, trazabilidad NFC y mantenimiento inteligente en tiempo real.",
      colProducto: "Producto",
      colEmpresa: "Empresa",
      linkContacto: "Contacto",
      linkSoporte: "Soporte",
      linkPrivacy: "Política de Privacidad",
      linkTerms: "Términos",
      copyright: "© 2026 ATLAS Industrial. Hecho en Argentina",
      rights: "Todos los derechos reservados.",
    },
    modal: {
      title: "Solicitar Demo de ATLAS",
      description:
        "Completá el formulario y un especialista se pondrá en contacto con vos en menos de 24 hs.",
      nombre: "Nombre Completo *",
      nombrePlaceholder: "Juan Pérez",
      email: "Email Corporativo *",
      emailPlaceholder: "nombre@empresa.com",
      empresa: "Empresa *",
      empresaPlaceholder: "Nombre de la empresa",
      telefono: "Teléfono *",
      telefonoPlaceholder: "+54 11 1234-5678",
      provincia: "Provincia *",
      provinciaPlaceholder: "Seleccioná una provincia",
      mensaje: "Mensaje (opcional)",
      mensajePlaceholder:
        "Contanos sobre tu planta, cantidad de activos, industria...",
      errNombre: "Ingresá tu nombre completo.",
      errEmail: "Email corporativo inválido.",
      errEmpresa: "Ingresá el nombre de la empresa.",
      errTelefono: "Ingresá un teléfono válido.",
      errProvincia: "Seleccioná una provincia.",
      btnCancel: "Cancelar",
      btnSubmit: "Enviar Solicitud",
      btnSending: "Enviando...",
      btnClose: "Cerrar",
      successTitle: "¡Solicitud recibida!",
      successSubtitle: "Gracias por tu interés en ATLAS. Te contactamos muy pronto.",
      toastSuccessTitle: "¡Solicitud enviada!",
      toastSuccessDesc:
        "Un especialista de ATLAS se pondrá en contacto con vos.",
      toastErrorTitle: "Error al enviar",
      toastErrorDesc: "No pudimos enviar tu solicitud. Intentá nuevamente.",
    },
    stickyCta: "Solicitá tu Demo",
    sectors: {
      badge: "Sectores",
      titleA: "Construido para los",
      titleB: "sectores industriales más exigentes",
      subtitle:
        "ATLAS se adapta a los estándares y protocolos de cada industria, con plantillas y checklists listos para usar.",
      s1: {
        name: "Minería",
        desc: "Trazabilidad de flota pesada, perforadoras y bombas en yacimientos de altura.",
      },
      s2: {
        name: "Oil & Gas",
        desc: "Mantenimiento preventivo y correctivo en campos, plantas y ductos.",
      },
      s3: {
        name: "Manufactura",
        desc: "Digitalización de líneas automotrices, alimenticias y farmacéuticas.",
      },
      s4: {
        name: "Infraestructura",
        desc: "Gestión de activos en energía, transporte y obras públicas.",
      },
    },
    testimonials: {
      badge: "Testimonios",
      titleA: "Lo que dicen los",
      titleB: "equipos que ya usan ATLAS",
      prev: "Anterior",
      next: "Siguiente",
      items: [
        {
          quote:
            "Pasamos de planillas en papel a un sistema auditable en menos de dos meses. Hoy cada intervención queda trazada al activo y al técnico.",
          author: "Martín Aguirre",
          role: "Jefe de Mantenimiento",
          company: "Minera Andes · Jujuy",
        },
        {
          quote:
            "ATLAS nos permitió estandarizar los checklists de cuatro parques eólicos. La reducción en tiempos de respuesta fue inmediata.",
          author: "Laura Benítez",
          role: "Gerente de Operaciones",
          company: "Patagonia Wind · Chubut",
        },
        {
          quote:
            "La app offline es un diferencial enorme en planta. Los técnicos la adoptaron en la primera semana.",
          author: "Ricardo Fernández",
          role: "Director Industrial",
          company: "AutoPart SA · Córdoba",
        },
      ],
    },
    clientLogin: "Acceso Clientes",
    footer2: {
      colRecursos: "Recursos",
      linkDocs: "Documentación Técnica",
      linkSupport: "Soporte Técnico",
      linkStatus: "Estado del Sistema",
    },
    docs: {
      title: "Documentación ATLAS",
      tagline: "Asset Traceability & Logistics Analytics System",
      backHome: "← Volver a la página principal",
      tocTitle: "En esta página",
      sections: {
        intro: {
          title: "Introducción al Ecosistema ATLAS",
          p1:
            "ATLAS es una plataforma de gestión de mantenimiento centrada en el activo. Combina identificación física mediante tags NFC, procedimientos estructurados (checklists) y ejecución móvil para crear un proceso de mantenimiento auditable, repetible y medible.",
          p2:
            "En entornos industriales, logísticos y de infraestructura, el mantenimiento suele operar con registros manuales y dispersos. ATLAS elimina la identificación incierta, los procedimientos desestructurados, la trazabilidad pobre, la falta de evidencia y los patrones invisibles de falla.",
        },
        nfc: {
          title: "Guía: Escaneo NFC",
          p1:
            "Cada activo se identifica con una etiqueta NFC única registrada en ATLAS. El técnico acerca el teléfono a la etiqueta y la app carga automáticamente el activo, su historial y los checklists disponibles.",
          steps: [
            "Adheri la etiqueta NFC en una zona limpia, accesible y fuera de metal ferromagnético directo.",
            "Registrá el ID de la etiqueta en el portal web asociándolo al activo.",
            "El técnico escanea con la app Android (NFC habilitado).",
            "ATLAS valida rol y especialidad antes de mostrar el checklist correspondiente.",
          ],
        },
        checklists: {
          title: "Guía: Checklists Inteligentes",
          p1:
            "Los checklists de ATLAS son plantillas parametrizables con pasos obligatorios, capturas de evidencia, lecturas numéricas y observaciones. Soportan modo offline-first.",
          steps: [
            "Creá plantillas por tipo de activo y especialidad desde el portal web.",
            "Definí pasos: lectura, foto, checkbox, firma, texto libre.",
            "Asigná la plantilla a uno o varios activos.",
            "El técnico ejecuta en campo; la app sincroniza al recuperar red.",
          ],
        },
        api: {
          title: "API de Integración",
          p1:
            "ATLAS expone una API REST autenticada (Bearer token) para integrar con ERPs, CMMS y BI. Endpoints clave:",
          endpoints: [
            { method: "GET", path: "/api/v1/assets", desc: "Listado de activos con filtros por sitio, área y especialidad." },
            { method: "POST", path: "/api/v1/checklists", desc: "Crear o actualizar una plantilla de checklist." },
            { method: "GET", path: "/api/v1/events", desc: "Consultar eventos de mantenimiento y hallazgos." },
            { method: "POST", path: "/api/v1/webhooks", desc: "Suscribirse a notificaciones (lead, falla crítica, auditoría)." },
          ],
          note:
            "Solicitá tu token de acceso escribiendo a soporte@atlas-system.net. La documentación completa está disponible en la consola del cliente.",
        },
      },
      nav: {
        intro: "Introducción",
        nfc: "Escaneo NFC",
        checklists: "Checklists",
        api: "API",
      },
    },
  },

  pt: {
    lang: "pt-BR",
    nav: {
      soluciones: "Soluções",
      casos: "Casos de Sucesso",
      sectores: "Setores",
      recursos: "Recursos",
      openMenu: "Abrir menu",
    },
    cta: {
      requestDemo: "Solicite uma Demo",
      learnMore: "Saiba mais",
      requestDemoLong: "Solicite uma Demo",
      whatsapp: "Fale pelo WhatsApp",
      languageSwitcher: "Mudar idioma",
    },
    hero: {
      title1: "Controle Total",
      title2a: "dos seus",
      title2b: "Ativos Industriais",
      subtitle: "Rastreabilidade e Manutenção Inteligente em Tempo Real.",
    },
    features: {
      f1: { title: "Escaneie & Verifique", desc: "Identifique cada ativo com NFC." },
      f2: {
        title: "Checklists Inteligentes",
        desc: "Garanta processos claros e seguros.",
      },
      f3: { title: "Analise & Decida", desc: "Detecte falhas e padrões críticos." },
    },
    whatIs: {
      badge: "O que é ATLAS?",
      titleA: "Uma plataforma centrada no",
      titleB: "ativo industrial",
      paragraphs: [
        "A **ATLAS** é uma plataforma de gestão de manutenção centrada no ativo. Combina identificação física via tags **NFC**, procedimentos estruturados (**checklists**) e execução móvel para criar um processo auditável, repetível e mensurável.",
        "Em ambientes industriais, logísticos e de infraestrutura, a manutenção costuma operar com registros manuais e dispersos. A ATLAS elimina a **identificação incerta**, os **procedimentos desestruturados**, a **rastreabilidade fraca**, a **falta de evidência** e os **padrões invisíveis** de falha.",
        "O resultado: sua equipe de campo trabalha com a mesma informação da gerência, em tempo real, mesmo em planta sem sinal.",
      ],
    },
    faq: {
      badge: "Perguntas-Chave",
      titleA: "O que a ATLAS",
      titleB: "permite saber?",
      subtitle:
        "As perguntas que toda gerência industrial deveria responder em menos de 30 segundos. Toque em cada uma para ver como a ATLAS as resolve.",
      items: [
        { q: "Qual ativo foi intervindo, quem fez e quando?", a: "Cada intervenção fica associada ao ativo (via NFC), ao técnico (login + papel) e ao timestamp do servidor. Rastreabilidade completa sem papel." },
        { q: "Os passos corretos do procedimento foram seguidos?", a: "Os checklists são obrigatórios passo a passo. Se faltar leitura, foto ou assinatura, o app não permite encerrar a tarefa." },
        { q: "Quais falhas estão se repetindo e onde?", a: "O motor de análise cruza achados por ativo, área e especialidade. Você detecta padrões críticos antes da parada não programada." },
        { q: "Qual é o custo real de manter cada ativo?", a: "A ATLAS consolida horas-homem, peças consumidas e frequência de intervenção por ativo. Decisões de substituição com dados." },
        { q: "A planta está pronta para uma auditoria externa?", a: "Relatórios exportáveis com assinatura digital, evidência fotográfica, leituras numéricas e rastreabilidade temporal. 100% auditáveis." },
        { q: "Meus técnicos trabalham igual em zonas sem sinal?", a: "Sim. O app é offline-first: executa checklists, captura fotos e grava leituras sem rede, e sincroniza ao reconectar." },
      ],
    },
    howItWorks: {
      badge: "Como Funciona",
      titleA: "A ATLAS em 10 passos,",
      titleB: "do ativo ao relatório",
      subtitle: "Um fluxo claro e previsível que sua equipe adota em um único turno de campo.",
      colStep: "Passo",
      colAction: "Ação",
      colDetail: "Detalhe",
      steps: [
        { action: "Identificação física do ativo", detail: "Uma tag NFC única é colada em área limpa e acessível do ativo." },
        { action: "Registro no portal", detail: "O supervisor associa a tag ao ativo, área, especialidade e templates de checklist." },
        { action: "Atribuição de checklists", detail: "Templates parametrizáveis são vinculados a um ou vários ativos por tipo e criticidade." },
        { action: "Leitura NFC em campo", detail: "O técnico aproxima o celular da tag e o app carrega ativo, histórico e tarefas pendentes." },
        { action: "Validação de papel", detail: "A ATLAS valida especialidade e permissões antes de exibir o checklist correspondente." },
        { action: "Execução passo a passo", detail: "Leituras, fotos, assinaturas e observações são capturadas em ordem com campos obrigatórios." },
        { action: "Modo offline", detail: "Sem sinal, o app salva tudo localmente. Nada se perde, nada interrompe a operação." },
        { action: "Sincronização automática", detail: "Ao recuperar conectividade, a ATLAS envia tudo ao servidor com timestamp original." },
        { action: "Análise & detecção de padrões", detail: "O motor cruza dados por ativo, área e especialidade para revelar falhas recorrentes." },
        { action: "Relatório & auditoria", detail: "Relatórios exportáveis com evidência, assinatura digital e rastreabilidade total." },
      ],
    },
    areas: {
      badge: "O que a ATLAS pode gerenciar?",
      titleA: "Qualquer ativo crítico,",
      titleB: "em qualquer indústria",
      subtitle: "De frota pesada a linhas de produção, a ATLAS se adapta aos ativos que sustentam sua operação.",
      items: [
        { title: "Equipamentos de produção", desc: "Bombas, motores, compressores e maquinário de linha com manutenção preventiva e corretiva." },
        { title: "Frota pesada e veicular", desc: "Caminhões, perfuratrizes, guindastes e maquinário móvel com rastreabilidade de horas e quilometragem." },
        { title: "Ativos elétricos", desc: "Transformadores, quadros, geradores e sistemas elétricos com inspeções programadas." },
        { title: "Instalações de planta", desc: "Tubulações, tanques, válvulas e estruturas civis com inspeção visual + leituras." },
        { title: "Ferramentas críticas", desc: "Torquímetros, manômetros e instrumental com calibração e vencimentos controlados." },
        { title: "Estoque e peças", desc: "Controle de inventário associado a cada intervenção, com consumo rastreável por ativo." },
        { title: "Segurança e higiene", desc: "Checklists de EPI, permissões de trabalho, bloqueio e etiquetagem (LOTO) auditáveis." },
        { title: "Calibrações e ensaios", desc: "Leituras numéricas com tolerâncias parametrizadas e alertas fora de faixa." },
        { title: "Edifícios e facilities", desc: "Manutenção de ar, iluminação, sanitários e áreas comuns em complexos industriais." },
        { title: "Energias renováveis", desc: "Aerogeradores, painéis solares e instalações distribuídas em sites remotos." },
        { title: "Serviços terceirizados", desc: "Rastreabilidade de contratados: quem entrou, o que fez e com qual evidência." },
      ],
    },
    checklistsTable: {
      badge: "Checklists Personalizados",
      titleA: "Templates adaptados",
      titleB: "à sua operação",
      subtitle: "A ATLAS suporta qualquer tipo de passo obrigatório. Crie o template uma vez e replique em milhares de intervenções.",
      colType: "Tipo de Passo",
      colExample: "Exemplo",
      colBenefit: "Benefício",
      rows: [
        { type: "Leitura numérica", example: "Pressão, temperatura, vibração, torque", benefit: "Tolerâncias parametrizadas; alerta automático fora de faixa." },
        { type: "Captura fotográfica", example: "Estado da bomba antes e depois", benefit: "Evidência visual com timestamp e geolocalização." },
        { type: "Checkbox de verificação", example: "Limpeza, ajuste, lubrificação", benefit: "Tarefas obrigatórias; não fecha o checklist sem concluir." },
        { type: "Assinatura digital", example: "Conformidade do técnico e supervisor", benefit: "Validação legal com selo horário do servidor." },
        { type: "Texto livre e observações", example: "Notas sobre achados imprevistos", benefit: "Contexto qualitativo enriquecido pelo técnico." },
        { type: "Leitura QR/NFC", example: "Identificação de peça consumida", benefit: "Rastreabilidade de estoque atrelada ao ativo intervindo." },
      ],
    },
    offline: {
      badge: "Funcionamento Sem Conexão",
      titleA: "Projetado para",
      titleB: "ambientes sem sinal",
      subtitle: "Plantas remotas, túneis, depósitos, parques eólicos. Sua equipe trabalha igual, com ou sem rede.",
      items: [
        { title: "Captura 100% offline", desc: "Leituras, fotos e assinaturas são salvas localmente no dispositivo do técnico." },
        { title: "Sincronização inteligente", desc: "Ao recuperar conectividade, a ATLAS envia tudo automaticamente sem intervenção do usuário." },
        { title: "Cache local criptografado", desc: "Os dados ficam protegidos no dispositivo com criptografia AES padrão da indústria." },
        { title: "Integridade de timestamp", desc: "O selo horário da captura é preservado, não sobrescrito ao sincronizar." },
      ],
    },
    metrics: {
      qPre: "",
      qStrong1: "Você realmente sabe",
      qMid: "o que está acontecendo na",
      qStrong2: "sua planta?",
      m1: "Rastreabilidade",
      m2: "Falhas Recorrentes",
      m3: "Evidência de Manutenção",
      m4: "Custos Operacionais",
    },
    showcase: {
      titleA: "Leve sua Manutenção ao",
      titleB: "Próximo Nível",
      subtitle:
        "Capacite suas equipes de campo com uma ferramenta robusta, offline-first e pensada para ambientes industriais exigentes.",
      bullets: [
        "Escaneie NFC no Ativo",
        "Checklists Passo a Passo",
        "Relatórios e Alertas em Tempo Real",
        "Histórico de Manutenções",
      ],
    },
    cases: {
      badge: "Casos de Sucesso",
      titleA: "Resultados comprovados na",
      titleB: "indústria latino-americana",
      subtitle:
        "Empresas líderes de diversos setores já estão transformando sua operação com a ATLAS. Veja alguns resultados reais.",
      c1: {
        industry: "Mineração",
        title: "Lavra em Alta Altitude",
        resultLabel: "disponibilidade de frota",
        desc:
          "Otimização de ativos em lavras de alta altitude. Alcançamos +25% de disponibilidade de frota com rastreabilidade NFC.",
      },
      c2: {
        industry: "Energia",
        title: "Parques Eólicos",
        resultLabel: "tempo de resposta",
        desc:
          "Controle de manutenção preventiva em parques eólicos. Redução de 40% no tempo de resposta a falhas críticas.",
      },
      c3: {
        industry: "Manufatura",
        title: "Planta Automotiva",
        resultLabel: "conformidade auditoria",
        desc:
          "Digitalização total de planta automotiva. Eliminação de registros em papel e 100% de conformidade com normas de auditoria.",
      },
    },
    finalCta: {
      titleA: "Leve sua Manutenção ao",
      titleB: "Próximo Nível",
      subtitle:
        "Solicite uma demo personalizada e descubra como digitalizar a manutenção da sua planta em minutos.",
    },
    footer: {
      tagline:
        "Plataforma industrial para gestão de ativos, rastreabilidade NFC e manutenção inteligente em tempo real.",
      colProducto: "Produto",
      colEmpresa: "Empresa",
      linkContacto: "Contato",
      linkSoporte: "Suporte",
      linkPrivacy: "Política de Privacidade",
      linkTerms: "Termos",
      copyright: "© 2026 ATLAS Industrial. Hecho en Argentina",
      rights: "Todos os direitos reservados.",
    },
    modal: {
      title: "Solicitar Demo da ATLAS",
      description:
        "Preencha o formulário e um especialista entrará em contato em até 24 h.",
      nombre: "Nome Completo *",
      nombrePlaceholder: "João Silva",
      email: "Email Corporativo *",
      emailPlaceholder: "nome@empresa.com",
      empresa: "Empresa *",
      empresaPlaceholder: "Nome da empresa",
      telefono: "Telefone *",
      telefonoPlaceholder: "+55 11 91234-5678",
      provincia: "Província *",
      provinciaPlaceholder: "Selecione uma província",
      mensaje: "Mensagem (opcional)",
      mensajePlaceholder:
        "Conte sobre sua planta, quantidade de ativos, indústria...",
      errNombre: "Informe seu nome completo.",
      errEmail: "Email corporativo inválido.",
      errEmpresa: "Informe o nome da empresa.",
      errTelefono: "Informe um telefone válido.",
      errProvincia: "Selecione uma província.",
      btnCancel: "Cancelar",
      btnSubmit: "Enviar Solicitação",
      btnSending: "Enviando...",
      btnClose: "Fechar",
      successTitle: "Solicitação recebida!",
      successSubtitle:
        "Obrigado pelo interesse na ATLAS. Entraremos em contato em breve.",
      toastSuccessTitle: "Solicitação enviada!",
      toastSuccessDesc:
        "Um especialista da ATLAS entrará em contato com você.",
      toastErrorTitle: "Erro ao enviar",
      toastErrorDesc:
        "Não foi possível enviar sua solicitação. Tente novamente.",
    },
    stickyCta: "Solicite uma Demo",
    sectors: {
      badge: "Setores",
      titleA: "Construído para os",
      titleB: "setores industriais mais exigentes",
      subtitle:
        "A ATLAS se adapta aos padrões e protocolos de cada indústria, com templates e checklists prontos para usar.",
      s1: { name: "Mineração", desc: "Rastreabilidade de frota pesada, perfuratrizes e bombas em lavras de alta altitude." },
      s2: { name: "Óleo & Gás", desc: "Manutenção preventiva e corretiva em campos, plantas e dutos." },
      s3: { name: "Manufatura", desc: "Digitalização de linhas automotivas, alimentícias e farmacêuticas." },
      s4: { name: "Infraestrutura", desc: "Gestão de ativos em energia, transporte e obras públicas." },
    },
    testimonials: {
      badge: "Depoimentos",
      titleA: "O que dizem as",
      titleB: "equipes que já usam a ATLAS",
      prev: "Anterior",
      next: "Próximo",
      items: [
        { quote: "Passamos de planilhas em papel para um sistema auditável em menos de dois meses. Hoje cada intervenção fica rastreada ao ativo e ao técnico.", author: "Martín Aguirre", role: "Chefe de Manutenção", company: "Minera Andes · Jujuy" },
        { quote: "A ATLAS nos permitiu padronizar os checklists de quatro parques eólicos. A redução nos tempos de resposta foi imediata.", author: "Laura Benítez", role: "Gerente de Operações", company: "Patagonia Wind · Chubut" },
        { quote: "O app offline é um diferencial enorme em planta. Os técnicos adotaram na primeira semana.", author: "Ricardo Fernández", role: "Diretor Industrial", company: "AutoPart SA · Córdoba" },
      ],
    },
    clientLogin: "Acesso Clientes",
    footer2: {
      colRecursos: "Recursos",
      linkDocs: "Documentação Técnica",
      linkSupport: "Suporte Técnico",
      linkStatus: "Status do Sistema",
    },
    docs: {
      title: "Documentação ATLAS",
      tagline: "Asset Traceability & Logistics Analytics System",
      backHome: "← Voltar à página inicial",
      tocTitle: "Nesta página",
      sections: {
        intro: {
          title: "Introdução ao Ecossistema ATLAS",
          p1: "A ATLAS é uma plataforma de gestão de manutenção centrada no ativo. Combina identificação física via tags NFC, procedimentos estruturados (checklists) e execução móvel para criar um processo auditável, repetível e mensurável.",
          p2: "Em ambientes industriais, logísticos e de infraestrutura, a manutenção costuma operar com registros manuais. A ATLAS elimina identificação incerta, procedimentos desestruturados, baixa rastreabilidade, falta de evidência e padrões de falha invisíveis.",
        },
        nfc: {
          title: "Guia: Leitura NFC",
          p1: "Cada ativo é identificado com uma tag NFC única registrada na ATLAS. O técnico aproxima o celular e o app carrega o ativo, histórico e checklists automaticamente.",
          steps: [
            "Cole a tag NFC em área limpa, acessível e fora de metal ferromagnético direto.",
            "Registre o ID da tag no portal web associando-o ao ativo.",
            "O técnico lê com o app Android (NFC habilitado).",
            "A ATLAS valida papel e especialidade antes de mostrar o checklist.",
          ],
        },
        checklists: {
          title: "Guia: Checklists Inteligentes",
          p1: "Os checklists da ATLAS são templates parametrizáveis com passos obrigatórios, evidências, leituras numéricas e observações. Suporte offline-first.",
          steps: [
            "Crie templates por tipo de ativo e especialidade no portal web.",
            "Defina passos: leitura, foto, checkbox, assinatura, texto livre.",
            "Atribua o template a um ou vários ativos.",
            "O técnico executa em campo; o app sincroniza ao recuperar rede.",
          ],
        },
        api: {
          title: "API de Integração",
          p1: "A ATLAS expõe uma API REST autenticada (Bearer token) para integração com ERPs, CMMS e BI. Endpoints principais:",
          endpoints: [
            { method: "GET", path: "/api/v1/assets", desc: "Lista de ativos com filtros por site, área e especialidade." },
            { method: "POST", path: "/api/v1/checklists", desc: "Criar ou atualizar um template de checklist." },
            { method: "GET", path: "/api/v1/events", desc: "Consultar eventos de manutenção e achados." },
            { method: "POST", path: "/api/v1/webhooks", desc: "Assinar notificações (lead, falha crítica, auditoria)." },
          ],
          note: "Solicite seu token escrevendo para soporte@atlas-system.net. A documentação completa está no console do cliente.",
        },
      },
      nav: { intro: "Introdução", nfc: "Leitura NFC", checklists: "Checklists", api: "API" },
    },
  },

  en: {
    lang: "en",
    nav: {
      soluciones: "Solutions",
      casos: "Case Studies",
      sectores: "Industries",
      recursos: "Resources",
      openMenu: "Open menu",
    },
    cta: {
      requestDemo: "Request a Demo",
      learnMore: "Learn more",
      requestDemoLong: "Request a Demo",
      whatsapp: "Contact via WhatsApp",
      languageSwitcher: "Switch language",
    },
    hero: {
      title1: "ATLAS",
      title2a: "Digital platform for",
      title2b: "operational traceability and field management control",
      subtitle: "NFC on the asset, offline-first checklists, and auditable reporting in real time.",
    },
    features: {
      f1: { title: "Scan & Verify", desc: "Identify every asset with NFC." },
      f2: {
        title: "Smart Checklists",
        desc: "Enforce clear and safe processes.",
      },
      f3: { title: "Analyze & Decide", desc: "Detect failures and critical patterns." },
    },
    whatIs: {
      badge: "What is ATLAS?",
      titleA: "An asset-centric platform for",
      titleB: "industrial operations",
      paragraphs: [
        "**ATLAS** is an asset-centric maintenance platform. It combines physical identification through **NFC tags**, structured procedures (**checklists**), and mobile execution to create an auditable, repeatable, and measurable process.",
        "Across industrial, logistics, and infrastructure environments, maintenance typically runs on manual, scattered records. ATLAS eliminates **uncertain identification**, **unstructured procedures**, **poor traceability**, **missing evidence**, and **invisible failure patterns**.",
        "The result: your field team operates with the same information as management, in real time — even at plants without signal.",
      ],
    },
    faq: {
      badge: "Key Questions",
      titleA: "What does ATLAS",
      titleB: "let you know?",
      subtitle:
        "The questions every industrial leadership team should answer in under 30 seconds. Tap each one to see how ATLAS solves it.",
      items: [
        { q: "Which asset was serviced, by whom, and when?", a: "Every intervention is linked to the asset (via NFC), the technician (login + role), and the server timestamp. Full traceability — no paper forms." },
        { q: "Were the correct procedure steps followed?", a: "Checklists are mandatory and step-by-step. If a reading, photo, or signature is missing, the app blocks closing the task." },
        { q: "Which failures are recurring and where?", a: "The analytics engine cross-references findings by asset, area, and specialty. You spot critical patterns before they become unplanned downtime." },
        { q: "What is the real cost of maintaining each asset?", a: "ATLAS consolidates labor hours, parts consumed, and intervention frequency per asset. Replacement decisions based on data, not intuition." },
        { q: "Is the plant ready for an external audit?", a: "Exportable reports with digital signatures, photo evidence, numeric readings, and time traceability. Fully ISO/IRAM auditable." },
        { q: "Do technicians work the same way without signal?", a: "Yes. The app is offline-first: it runs checklists, captures photos, and records readings without network, syncing as soon as connectivity is restored." },
      ],
    },
    howItWorks: {
      badge: "How It Works",
      titleA: "ATLAS in 10 steps,",
      titleB: "from asset to report",
      subtitle: "A clear, predictable workflow your team adopts in a single field shift.",
      colStep: "Step",
      colAction: "Action",
      colDetail: "Detail",
      steps: [
        { action: "Physical asset identification", detail: "A unique NFC tag is stuck on a clean, accessible area of the asset." },
        { action: "Portal registration", detail: "The supervisor links the tag to the asset, its area, specialty, and checklist templates." },
        { action: "Checklist assignment", detail: "Parameterizable templates are bound to one or many assets by type and criticality." },
        { action: "Field NFC scan", detail: "The technician taps the phone to the tag and the app loads the asset, history, and pending tasks." },
        { action: "Role validation", detail: "ATLAS verifies specialty and permissions before showing the matching checklist." },
        { action: "Step-by-step execution", detail: "Readings, photos, signatures, and notes are captured in order, with mandatory fields." },
        { action: "Offline mode", detail: "Without signal, the app stores everything locally. Nothing is lost, nothing is interrupted." },
        { action: "Automatic sync", detail: "When connectivity returns, ATLAS uploads everything to the server with the original timestamp." },
        { action: "Analysis & pattern detection", detail: "The engine cross-references data by asset, area, and specialty to surface recurring failures." },
        { action: "Report & audit", detail: "Exportable reports with evidence, digital signature, and full traceability for internal or external audits." },
      ],
    },
    areas: {
      badge: "What ATLAS Can Manage",
      titleA: "Any critical asset,",
      titleB: "in any industry",
      subtitle: "From heavy fleet to production lines, ATLAS adapts to the assets that keep your operation running.",
      items: [
        { title: "Production equipment", desc: "Pumps, motors, compressors, and line machinery with preventive and corrective maintenance." },
        { title: "Heavy & vehicle fleet", desc: "Trucks, drill rigs, cranes, and mobile machinery with hour and mileage traceability." },
        { title: "Electrical assets", desc: "Transformers, panels, generators, and electrical systems with scheduled inspections." },
        { title: "Plant facilities", desc: "Piping, tanks, valves, and civil structures with visual inspection + readings." },
        { title: "Critical tools", desc: "Torque wrenches, pressure gauges, and instruments with controlled calibration and expirations." },
        { title: "Stock & spare parts", desc: "Inventory control linked to each intervention, with consumption traceable per asset." },
        { title: "Safety & hygiene", desc: "PPE checklists, work permits, lockout-tagout (LOTO) — fully auditable." },
        { title: "Calibrations & testing", desc: "Numeric readings with parameterized tolerances and automatic out-of-range alerts." },
        { title: "Buildings & facilities", desc: "HVAC, lighting, sanitation, and common areas across industrial complexes." },
        { title: "Renewable energy", desc: "Wind turbines, solar panels, and distributed installations at remote sites." },
        { title: "Third-party services", desc: "Contractor traceability: who entered, what they did, and what evidence backs the task." },
      ],
    },
    checklistsTable: {
      badge: "Custom Checklists",
      titleA: "Templates tailored to",
      titleB: "your operation",
      subtitle: "ATLAS supports any kind of mandatory step. Design the template once and replicate it across thousands of interventions.",
      colType: "Step Type",
      colExample: "Example",
      colBenefit: "Benefit",
      rows: [
        { type: "Numeric reading", example: "Pressure, temperature, vibration, torque", benefit: "Parameterized tolerances; automatic out-of-range alerts." },
        { type: "Photo capture", example: "Pump condition before and after", benefit: "Visual evidence with timestamp and geolocation." },
        { type: "Verification checkbox", example: "Cleaning, adjustment, lubrication", benefit: "Mandatory tasks; checklist cannot be closed until completed." },
        { type: "Digital signature", example: "Technician and supervisor sign-off", benefit: "Legal validation with server-side timestamp." },
        { type: "Free text & notes", example: "Observations on unexpected findings", benefit: "Qualitative context enriched by the technician." },
        { type: "QR/NFC scan", example: "Spare part identification", benefit: "Inventory traceability tied to the serviced asset." },
      ],
    },
    offline: {
      badge: "Offline Operation",
      titleA: "Built for",
      titleB: "no-signal environments",
      subtitle: "Remote plants, tunnels, warehouses, wind farms. Your team works the same, with or without network.",
      items: [
        { title: "100% offline capture", desc: "Readings, photos, and signatures are stored locally on the technician's device." },
        { title: "Smart synchronization", desc: "Once connectivity returns, ATLAS uploads everything automatically, no user action required." },
        { title: "Encrypted local cache", desc: "Data stays protected on the device with industry-standard AES encryption." },
        { title: "Timestamp integrity", desc: "The original capture timestamp is preserved, not overwritten on sync." },
      ],
    },
    metrics: {
      qPre: "",
      qStrong1: "Do you really know",
      qMid: "what is happening at",
      qStrong2: "your plant?",
      m1: "Traceability",
      m2: "Recurring Failures",
      m3: "Maintenance Evidence",
      m4: "Operating Costs",
    },
    showcase: {
      titleA: "Take your Maintenance to the",
      titleB: "Next Level",
      subtitle:
        "Empower your field teams with a robust, offline-first tool built for demanding industrial environments.",
      bullets: [
        "Scan NFC on the Asset",
        "Step-by-Step Checklists",
        "Real-time Reports & Alerts",
        "Maintenance History",
      ],
    },
    cases: {
      badge: "Case Studies",
      titleA: "Proven results in the",
      titleB: "industrial sector",
      subtitle:
        "Leading companies across multiple sectors are already transforming their operations with ATLAS. Here are some real results.",
      c1: {
        industry: "Mining",
        title: "High-altitude Sites",
        resultLabel: "fleet availability",
        desc:
          "Asset optimization at high-altitude mining sites. We achieved +25% fleet availability through NFC traceability.",
      },
      c2: {
        industry: "Energy",
        title: "Wind Farms",
        resultLabel: "response time",
        desc:
          "Preventive maintenance control at wind farms. 40% reduction in response time to critical failures.",
      },
      c3: {
        industry: "Manufacturing",
        title: "Automotive Plant",
        resultLabel: "audit compliance",
        desc:
          "Full digitalization of an automotive plant. Zero paper records and 100% compliance with audit standards.",
      },
    },
    finalCta: {
      titleA: "Take your Maintenance to the",
      titleB: "Next Level",
      subtitle:
        "Request a personalized demo and discover how to digitize your plant's maintenance in minutes.",
    },
    footer: {
      tagline:
        "Industrial platform for asset management, NFC traceability, and real-time smart maintenance.",
      colProducto: "Product",
      colEmpresa: "Company",
      linkContacto: "Contact",
      linkSoporte: "Support",
      linkPrivacy: "Privacy Policy",
      linkTerms: "Terms",
      copyright: "© 2026 ATLAS Industrial. Hecho en Argentina",
      rights: "All rights reserved.",
    },
    modal: {
      title: "Request an ATLAS Demo",
      description:
        "Fill out the form and a specialist will get in touch within 24 hours.",
      nombre: "Full Name *",
      nombrePlaceholder: "John Smith",
      email: "Business Email *",
      emailPlaceholder: "name@company.com",
      empresa: "Company *",
      empresaPlaceholder: "Company name",
      telefono: "Phone *",
      telefonoPlaceholder: "+1 555 000 0000",
      provincia: "Region *",
      provinciaPlaceholder: "Select a region",
      mensaje: "Message (optional)",
      mensajePlaceholder:
        "Tell us about your plant, number of assets, industry...",
      errNombre: "Please enter your full name.",
      errEmail: "Invalid business email.",
      errEmpresa: "Please enter your company name.",
      errTelefono: "Please enter a valid phone number.",
      errProvincia: "Please select a region.",
      btnCancel: "Cancel",
      btnSubmit: "Submit Request",
      btnSending: "Sending...",
      btnClose: "Close",
      successTitle: "Request received!",
      successSubtitle:
        "Thanks for your interest in ATLAS. We'll reach out very soon.",
      toastSuccessTitle: "Request submitted!",
      toastSuccessDesc: "An ATLAS specialist will contact you shortly.",
      toastErrorTitle: "Submission error",
      toastErrorDesc: "We couldn't submit your request. Please try again.",
    },
    stickyCta: "Request a Demo",
    sectors: {
      badge: "Industries",
      titleA: "Built for the",
      titleB: "most demanding industrial sectors",
      subtitle:
        "ATLAS adapts to every industry's standards and protocols, with ready-to-use templates and checklists.",
      s1: { name: "Mining", desc: "Traceability of heavy fleet, drill rigs, and pumps at high-altitude sites." },
      s2: { name: "Oil & Gas", desc: "Preventive and corrective maintenance across fields, plants, and pipelines." },
      s3: { name: "Manufacturing", desc: "Digitalization of automotive, food, and pharmaceutical production lines." },
      s4: { name: "Infrastructure", desc: "Asset management for energy, transport, and public works." },
    },
    testimonials: {
      badge: "Testimonials",
      titleA: "What",
      titleB: "teams using ATLAS are saying",
      prev: "Previous",
      next: "Next",
      items: [
        { quote: "We moved from paper forms to an auditable system in under two months. Every intervention is now traced to the asset and the technician.", author: "Martín Aguirre", role: "Maintenance Lead", company: "Minera Andes · Jujuy" },
        { quote: "ATLAS helped us standardize checklists across four wind farms. The drop in response times was immediate.", author: "Laura Benítez", role: "Operations Manager", company: "Patagonia Wind · Chubut" },
        { quote: "The offline app is a huge advantage on the plant floor. Technicians adopted it in the first week.", author: "Ricardo Fernández", role: "Industrial Director", company: "AutoPart SA · Córdoba" },
      ],
    },
    clientLogin: "Client Login",
    footer2: {
      colRecursos: "Resources",
      linkDocs: "Technical Documentation",
      linkSupport: "Technical Support",
      linkStatus: "System Status",
    },
    docs: {
      title: "ATLAS Documentation",
      tagline: "Asset Traceability & Logistics Analytics System",
      backHome: "← Back to main site",
      tocTitle: "On this page",
      sections: {
        intro: {
          title: "Introduction to the ATLAS Ecosystem",
          p1: "ATLAS is an asset-centric maintenance platform. It combines physical identification via NFC tags, structured procedures (checklists), and mobile execution to create an auditable, repeatable, measurable maintenance process.",
          p2: "Across industrial, logistics, and infrastructure environments, maintenance typically runs on manual, scattered records. ATLAS eliminates uncertain identification, unstructured procedures, poor traceability, missing evidence, and invisible failure patterns.",
        },
        nfc: {
          title: "Guide: NFC Scanning",
          p1: "Each asset is identified by a unique NFC tag registered in ATLAS. The technician taps the phone to the tag and the app loads the asset, its history, and the available checklists.",
          steps: [
            "Stick the NFC tag on a clean, accessible area away from direct ferromagnetic metal.",
            "Register the tag ID in the web portal, linking it to the asset.",
            "The technician scans with the Android app (NFC enabled).",
            "ATLAS validates role and specialty before showing the matching checklist.",
          ],
        },
        checklists: {
          title: "Guide: Smart Checklists",
          p1: "ATLAS checklists are parameterizable templates with mandatory steps, evidence capture, numeric readings, and observations. Offline-first by design.",
          steps: [
            "Create templates per asset type and specialty in the web portal.",
            "Define steps: reading, photo, checkbox, signature, free text.",
            "Assign the template to one or many assets.",
            "Technicians execute in the field; the app syncs when back online.",
          ],
        },
        api: {
          title: "Integration API",
          p1: "ATLAS exposes an authenticated REST API (Bearer token) for integration with ERPs, CMMS, and BI. Key endpoints:",
          endpoints: [
            { method: "GET", path: "/api/v1/assets", desc: "List of assets filtered by site, area, and specialty." },
            { method: "POST", path: "/api/v1/checklists", desc: "Create or update a checklist template." },
            { method: "GET", path: "/api/v1/events", desc: "Query maintenance events and findings." },
            { method: "POST", path: "/api/v1/webhooks", desc: "Subscribe to notifications (lead, critical failure, audit)." },
          ],
          note: "Request your access token at support@atlas-system.net. Full documentation is available in the customer console.",
        },
      },
      nav: { intro: "Introduction", nfc: "NFC Scanning", checklists: "Checklists", api: "API" },
    },
  },

  fr: {
    lang: "fr",
    nav: {
      soluciones: "Solutions",
      casos: "Études de Cas",
      sectores: "Secteurs",
      recursos: "Ressources",
      openMenu: "Ouvrir le menu",
    },
    cta: {
      requestDemo: "Demander une Démo",
      learnMore: "En savoir plus",
      requestDemoLong: "Demander une Démo",
      whatsapp: "Contacter par WhatsApp",
      languageSwitcher: "Changer de langue",
    },
    hero: {
      title1: "ATLAS",
      title2a: "Plateforme numérique de",
      title2b: "traçabilité opérationnelle et contrôle de gestion sur le terrain",
      subtitle: "NFC sur l'actif, checklists offline-first et reporting auditable en temps réel.",
    },
    features: {
      f1: {
        title: "Scannez & Vérifiez",
        desc: "Identifiez chaque actif avec NFC.",
      },
      f2: {
        title: "Checklists Intelligentes",
        desc: "Imposez des processus clairs et sûrs.",
      },
      f3: {
        title: "Analysez & Décidez",
        desc: "Détectez les pannes et schémas critiques.",
      },
    },
    whatIs: {
      badge: "Qu'est-ce qu'ATLAS ?",
      titleA: "Une plateforme centrée sur",
      titleB: "l'actif industriel",
      paragraphs: [
        "**ATLAS** est une plateforme de gestion de maintenance centrée sur l'actif. Elle combine identification physique via tags **NFC**, procédures structurées (**checklists**) et exécution mobile pour un processus auditable, reproductible et mesurable.",
        "Dans les environnements industriels, logistiques et d'infrastructure, la maintenance fonctionne souvent avec des registres manuels et dispersés. ATLAS élimine l'**identification incertaine**, les **procédures non structurées**, la **traçabilité faible**, le **manque de preuves** et les **schémas de pannes invisibles**.",
        "Le résultat : vos équipes terrain travaillent avec la même information que la direction, en temps réel — même dans des usines sans réseau.",
      ],
    },
    faq: {
      badge: "Questions Clés",
      titleA: "Que vous permet de savoir",
      titleB: "ATLAS ?",
      subtitle:
        "Les questions auxquelles toute direction industrielle devrait répondre en moins de 30 secondes. Cliquez sur chacune pour voir comment ATLAS les résout.",
      items: [
        { q: "Quel actif a été entretenu, par qui et quand ?", a: "Chaque intervention est liée à l'actif (via NFC), au technicien (login + rôle) et à l'horodatage serveur. Traçabilité totale, sans papier." },
        { q: "Les étapes correctes de la procédure ont-elles été suivies ?", a: "Les checklists sont obligatoires étape par étape. Sans lecture, photo ou signature, l'application bloque la clôture de la tâche." },
        { q: "Quelles pannes se répètent et où ?", a: "Le moteur d'analyse croise les constats par actif, zone et spécialité. Vous détectez les schémas critiques avant l'arrêt non programmé." },
        { q: "Quel est le coût réel d'entretien de chaque actif ?", a: "ATLAS consolide heures de main-d'œuvre, pièces consommées et fréquence d'intervention par actif. Décisions de remplacement basées sur les données." },
        { q: "L'usine est-elle prête pour un audit externe ?", a: "Rapports exportables avec signature numérique, preuves photos, lectures numériques et traçabilité temporelle. Entièrement auditables ISO." },
        { q: "Mes techniciens travaillent-ils pareil sans réseau ?", a: "Oui. L'application est offline-first : checklists, photos et lectures fonctionnent sans réseau, et se synchronisent dès reconnexion." },
      ],
    },
    howItWorks: {
      badge: "Comment Ça Marche",
      titleA: "ATLAS en 10 étapes,",
      titleB: "de l'actif au rapport",
      subtitle: "Un flux clair et prévisible que votre équipe adopte en une seule journée terrain.",
      colStep: "Étape",
      colAction: "Action",
      colDetail: "Détail",
      steps: [
        { action: "Identification physique de l'actif", detail: "Un tag NFC unique est collé sur une zone propre et accessible de l'actif." },
        { action: "Enregistrement dans le portail", detail: "Le superviseur associe le tag à l'actif, sa zone, sa spécialité et ses modèles de checklist." },
        { action: "Affectation des checklists", detail: "Modèles paramétrables liés à un ou plusieurs actifs selon type et criticité." },
        { action: "Scan NFC terrain", detail: "Le technicien approche le téléphone du tag ; l'app charge l'actif, l'historique et les tâches." },
        { action: "Validation du rôle", detail: "ATLAS vérifie spécialité et permissions avant d'afficher la checklist correspondante." },
        { action: "Exécution pas à pas", detail: "Lectures, photos, signatures et observations sont capturées dans l'ordre, avec champs obligatoires." },
        { action: "Mode hors-ligne", detail: "Sans réseau, l'application stocke tout localement. Rien ne se perd, rien ne s'interrompt." },
        { action: "Synchronisation automatique", detail: "Au retour du réseau, ATLAS envoie tout au serveur avec l'horodatage d'origine." },
        { action: "Analyse & détection de schémas", detail: "Le moteur croise les données par actif, zone et spécialité pour faire émerger les pannes récurrentes." },
        { action: "Rapport & audit", detail: "Rapports exportables avec preuves, signature numérique et traçabilité totale pour audits internes ou externes." },
      ],
    },
    areas: {
      badge: "Ce qu'ATLAS peut gérer",
      titleA: "Tout actif critique,",
      titleB: "dans toute industrie",
      subtitle: "Des flottes lourdes aux lignes de production, ATLAS s'adapte aux actifs qui font tourner votre exploitation.",
      items: [
        { title: "Équipements de production", desc: "Pompes, moteurs, compresseurs et machines de ligne avec maintenance préventive et corrective." },
        { title: "Flotte lourde & véhicules", desc: "Camions, foreuses, grues et engins mobiles avec traçabilité d'heures et kilométrage." },
        { title: "Actifs électriques", desc: "Transformateurs, tableaux, groupes électrogènes et systèmes électriques avec inspections programmées." },
        { title: "Installations d'usine", desc: "Tuyauteries, réservoirs, vannes et structures civiles avec inspection visuelle et lectures." },
        { title: "Outils critiques", desc: "Clés dynamométriques, manomètres et instrumentation avec calibration et échéances contrôlées." },
        { title: "Stock & pièces", desc: "Contrôle d'inventaire lié à chaque intervention, avec consommation traçable par actif." },
        { title: "Sécurité & hygiène", desc: "Checklists EPI, permis de travail, consignation (LOTO) entièrement auditables." },
        { title: "Calibrations & essais", desc: "Lectures numériques avec tolérances paramétrées et alertes hors plage automatiques." },
        { title: "Bâtiments & facilities", desc: "Maintenance de la climatisation, éclairage, sanitaires et zones communes dans les complexes industriels." },
        { title: "Énergies renouvelables", desc: "Éoliennes, panneaux solaires et installations distribuées sur sites distants." },
        { title: "Services sous-traités", desc: "Traçabilité des prestataires : qui est entré, ce qu'ils ont fait et avec quelles preuves." },
      ],
    },
    checklistsTable: {
      badge: "Checklists Personnalisés",
      titleA: "Modèles adaptés à",
      titleB: "votre exploitation",
      subtitle: "ATLAS supporte tout type d'étape obligatoire. Concevez le modèle une fois et répliquez-le sur des milliers d'interventions.",
      colType: "Type d'étape",
      colExample: "Exemple",
      colBenefit: "Bénéfice",
      rows: [
        { type: "Lecture numérique", example: "Pression, température, vibration, couple", benefit: "Tolérances paramétrées ; alerte automatique hors plage." },
        { type: "Capture photo", example: "État de la pompe avant et après", benefit: "Preuve visuelle avec horodatage et géolocalisation." },
        { type: "Case à cocher", example: "Nettoyage, ajustement, lubrification", benefit: "Tâches obligatoires ; la checklist ne se ferme pas sans validation." },
        { type: "Signature numérique", example: "Conformité technicien et superviseur", benefit: "Validation légale avec horodatage côté serveur." },
        { type: "Texte libre & notes", example: "Observations sur des constats imprévus", benefit: "Contexte qualitatif enrichi par le technicien." },
        { type: "Lecture QR/NFC", example: "Identification de la pièce consommée", benefit: "Traçabilité du stock liée à l'actif intervenu." },
      ],
    },
    offline: {
      badge: "Fonctionnement Hors-Ligne",
      titleA: "Conçu pour",
      titleB: "les environnements sans réseau",
      subtitle: "Usines isolées, tunnels, entrepôts, parcs éoliens. Votre équipe travaille pareil, avec ou sans réseau.",
      items: [
        { title: "Capture 100 % hors-ligne", desc: "Lectures, photos et signatures sont stockées localement sur l'appareil du technicien." },
        { title: "Synchronisation intelligente", desc: "Au retour de la connectivité, ATLAS téléverse tout automatiquement, sans action de l'utilisateur." },
        { title: "Cache local chiffré", desc: "Les données restent protégées sur l'appareil avec chiffrement AES standard industriel." },
        { title: "Intégrité d'horodatage", desc: "L'horodatage d'origine de la capture est préservé, jamais écrasé à la synchronisation." },
      ],
    },
    metrics: {
      qPre: "",
      qStrong1: "Savez-vous vraiment",
      qMid: "ce qui se passe dans",
      qStrong2: "votre usine ?",
      m1: "Traçabilité",
      m2: "Pannes Récurrentes",
      m3: "Preuve de Maintenance",
      m4: "Coûts Opérationnels",
    },
    showcase: {
      titleA: "Passez votre Maintenance au",
      titleB: "Niveau Supérieur",
      subtitle:
        "Dotez vos équipes terrain d'un outil robuste, offline-first, conçu pour les environnements industriels exigeants.",
      bullets: [
        "Scannez NFC sur l'Actif",
        "Checklists Pas à Pas",
        "Rapports & Alertes en Temps Réel",
        "Historique des Maintenances",
      ],
    },
    cases: {
      badge: "Études de Cas",
      titleA: "Des résultats prouvés dans",
      titleB: "l'industrie",
      subtitle:
        "Des entreprises leaders de plusieurs secteurs transforment déjà leurs opérations avec ATLAS. Voici des résultats concrets.",
      c1: {
        industry: "Mines",
        title: "Sites en Haute Altitude",
        resultLabel: "disponibilité de flotte",
        desc:
          "Optimisation des actifs en exploitation minière d'altitude. +25 % de disponibilité de flotte grâce à la traçabilité NFC.",
      },
      c2: {
        industry: "Énergie",
        title: "Parcs Éoliens",
        resultLabel: "temps de réponse",
        desc:
          "Contrôle de la maintenance préventive en parcs éoliens. 40 % de réduction du temps de réponse sur les pannes critiques.",
      },
      c3: {
        industry: "Industrie",
        title: "Usine Automobile",
        resultLabel: "conformité audit",
        desc:
          "Numérisation complète d'une usine automobile. Fin des registres papier et 100 % de conformité aux normes d'audit.",
      },
    },
    finalCta: {
      titleA: "Passez votre Maintenance au",
      titleB: "Niveau Supérieur",
      subtitle:
        "Demandez une démo personnalisée et découvrez comment digitaliser la maintenance de votre usine en quelques minutes.",
    },
    footer: {
      tagline:
        "Plateforme industrielle pour la gestion des actifs, la traçabilité NFC et la maintenance intelligente en temps réel.",
      colProducto: "Produit",
      colEmpresa: "Entreprise",
      linkContacto: "Contact",
      linkSoporte: "Support",
      linkPrivacy: "Politique de Confidentialité",
      linkTerms: "Conditions",
      copyright: "© 2026 ATLAS Industrial. Hecho en Argentina",
      rights: "Tous droits réservés.",
    },
    modal: {
      title: "Demander une Démo ATLAS",
      description:
        "Remplissez le formulaire et un spécialiste vous contactera sous 24 h.",
      nombre: "Nom Complet *",
      nombrePlaceholder: "Jean Dupont",
      email: "Email Professionnel *",
      emailPlaceholder: "nom@entreprise.com",
      empresa: "Entreprise *",
      empresaPlaceholder: "Nom de l'entreprise",
      telefono: "Téléphone *",
      telefonoPlaceholder: "+33 1 23 45 67 89",
      provincia: "Région *",
      provinciaPlaceholder: "Sélectionnez une région",
      mensaje: "Message (facultatif)",
      mensajePlaceholder:
        "Parlez-nous de votre usine, nombre d'actifs, secteur...",
      errNombre: "Veuillez saisir votre nom complet.",
      errEmail: "Email professionnel invalide.",
      errEmpresa: "Veuillez saisir le nom de l'entreprise.",
      errTelefono: "Veuillez saisir un téléphone valide.",
      errProvincia: "Veuillez sélectionner une région.",
      btnCancel: "Annuler",
      btnSubmit: "Envoyer la Demande",
      btnSending: "Envoi en cours...",
      btnClose: "Fermer",
      successTitle: "Demande reçue !",
      successSubtitle:
        "Merci de votre intérêt pour ATLAS. Nous vous contactons très prochainement.",
      toastSuccessTitle: "Demande envoyée !",
      toastSuccessDesc:
        "Un spécialiste ATLAS vous contactera très bientôt.",
      toastErrorTitle: "Erreur à l'envoi",
      toastErrorDesc:
        "Nous n'avons pas pu envoyer votre demande. Veuillez réessayer.",
    },
    stickyCta: "Demander une Démo",
    sectors: {
      badge: "Secteurs",
      titleA: "Conçu pour les",
      titleB: "secteurs industriels les plus exigeants",
      subtitle:
        "ATLAS s'adapte aux normes et protocoles de chaque industrie, avec des modèles et checklists prêts à l'emploi.",
      s1: { name: "Mines", desc: "Traçabilité des flottes lourdes, foreuses et pompes en sites d'altitude." },
      s2: { name: "Oil & Gas", desc: "Maintenance préventive et corrective sur gisements, usines et pipelines." },
      s3: { name: "Industrie", desc: "Numérisation de lignes automobile, agroalimentaire et pharmaceutique." },
      s4: { name: "Infrastructure", desc: "Gestion d'actifs en énergie, transport et travaux publics." },
    },
    testimonials: {
      badge: "Témoignages",
      titleA: "Ce que disent les",
      titleB: "équipes qui utilisent ATLAS",
      prev: "Précédent",
      next: "Suivant",
      items: [
        { quote: "Nous sommes passés des formulaires papier à un système auditable en moins de deux mois. Chaque intervention est tracée jusqu'à l'actif et au technicien.", author: "Martín Aguirre", role: "Responsable Maintenance", company: "Minera Andes · Jujuy" },
        { quote: "ATLAS nous a permis de standardiser les checklists de quatre parcs éoliens. La baisse du temps de réponse a été immédiate.", author: "Laura Benítez", role: "Responsable Opérations", company: "Patagonia Wind · Chubut" },
        { quote: "L'application hors-ligne est un énorme atout en usine. Les techniciens l'ont adoptée dès la première semaine.", author: "Ricardo Fernández", role: "Directeur Industriel", company: "AutoPart SA · Córdoba" },
      ],
    },
    clientLogin: "Espace Client",
    footer2: {
      colRecursos: "Ressources",
      linkDocs: "Documentation Technique",
      linkSupport: "Support Technique",
      linkStatus: "État du Système",
    },
    docs: {
      title: "Documentation ATLAS",
      tagline: "Asset Traceability & Logistics Analytics System",
      backHome: "← Retour au site principal",
      tocTitle: "Sur cette page",
      sections: {
        intro: {
          title: "Introduction à l'Écosystème ATLAS",
          p1: "ATLAS est une plateforme de gestion de maintenance centrée sur l'actif. Elle combine identification physique via tags NFC, procédures structurées (checklists) et exécution mobile pour un processus auditable, reproductible et mesurable.",
          p2: "Dans les environnements industriels, logistiques et d'infrastructure, la maintenance fonctionne souvent avec des registres manuels et dispersés. ATLAS élimine l'identification incertaine, les procédures non structurées, la traçabilité faible, le manque de preuves et les schémas de pannes invisibles.",
        },
        nfc: {
          title: "Guide : Scan NFC",
          p1: "Chaque actif est identifié par un tag NFC unique enregistré dans ATLAS. Le technicien approche le téléphone et l'app charge l'actif, son historique et les checklists disponibles.",
          steps: [
            "Collez le tag NFC dans une zone propre, accessible et éloignée de métal ferromagnétique direct.",
            "Enregistrez l'ID du tag dans le portail web en l'associant à l'actif.",
            "Le technicien scanne avec l'app Android (NFC activé).",
            "ATLAS valide rôle et spécialité avant d'afficher la checklist correspondante.",
          ],
        },
        checklists: {
          title: "Guide : Checklists Intelligentes",
          p1: "Les checklists ATLAS sont des modèles paramétrables avec étapes obligatoires, preuves, lectures numériques et observations. Fonctionnement offline-first.",
          steps: [
            "Créez des modèles par type d'actif et spécialité depuis le portail web.",
            "Définissez les étapes : lecture, photo, checkbox, signature, texte libre.",
            "Affectez le modèle à un ou plusieurs actifs.",
            "Les techniciens exécutent sur le terrain ; l'app synchronise dès le retour du réseau.",
          ],
        },
        api: {
          title: "API d'Intégration",
          p1: "ATLAS expose une API REST authentifiée (jeton Bearer) pour s'intégrer aux ERP, CMMS et BI. Endpoints clés :",
          endpoints: [
            { method: "GET", path: "/api/v1/assets", desc: "Liste des actifs avec filtres par site, zone et spécialité." },
            { method: "POST", path: "/api/v1/checklists", desc: "Créer ou mettre à jour un modèle de checklist." },
            { method: "GET", path: "/api/v1/events", desc: "Consulter les événements de maintenance et anomalies." },
            { method: "POST", path: "/api/v1/webhooks", desc: "S'abonner aux notifications (lead, panne critique, audit)." },
          ],
          note: "Demandez votre jeton d'accès à support@atlas-system.net. La documentation complète se trouve dans la console client.",
        },
      },
      nav: { intro: "Introduction", nfc: "Scan NFC", checklists: "Checklists", api: "API" },
    },
  },
};
